import type { DokumentEntwurf } from '../../dokumente/types';
import type { Unterlage } from '../types';

/**
 * Anlagenliste und Projektablage — die Regeln dazwischen.
 *
 * ═══ WOZU ES DIESE DATEI GIBT ═════════════════════════════════════════════
 * Kapitel 9 des Plans („Verwendete Unterlagen und Anlagen") war bis hierher
 * ein handgepflegter Text: wer „wird nachgereicht" nicht wieder abhakte,
 * druckte auf Dauer eine Behauptung. Der Plan soll aber AUSWEISEN, WAS
 * TATSAECHLICH VORLIEGT — und der einzige Ort, an dem das nachpruefbar ist,
 * ist die Projektablage (`project_documents`).
 *
 * Der Beleg ist deshalb `Unterlage.dokument_id`: ein gesetzter Verweis heisst
 * „liegt vor", ein fehlender heisst „liegt nicht vor". Das ist eine ANGABE
 * ueber die Aktenlage und keine Meinung.
 *
 * ═══ WARUM DIE REGEL HIER STEHT UND NICHT IM PDF-GENERATOR ════════════════
 * Der Generator baut keine Fachlogik nach. Im Handbuch-Modul hat genau das
 * einmal dazu gefuehrt, dass der Titel eines admin-gesperrten Dokuments
 * gedruckt wurde: der Generator kannte die Regel ein zweites Mal, und die
 * zweite Fassung war die aeltere. Kapitel 9 ruft deshalb `anlagenZeilen` auf
 * und entscheidet selbst nichts.
 *
 * ═══ DREI ZUSTAENDE, NICHT ZWEI ═══════════════════════════════════════════
 * `liegt_vor`        — es gibt ein Dokument in der Ablage.
 * `wird_nachgereicht`— es ist ausdruecklich als nachzureichend gekennzeichnet.
 * `nicht_abgelegt`   — weder noch.
 *
 * Der dritte Zustand ist der wichtige. Ihn auf „liegt vor" abzurunden hiesse,
 * eine unbelegte Behauptung zu drucken; ihn auf „wird nachgereicht"
 * abzurunden hiesse, jemandem eine Zusage zu unterstellen, die er nie gemacht
 * hat. Beide offenen Zustaende erscheinen rot — Regel 9 der Uebergabe: der
 * Plan zeigt auch das Offene.
 *
 * ═══ ADMIN-GESPERRTE DOKUMENTE ════════════════════════════════════════════
 * Ein Dokument mit `nur_admin` faellt fuer Nicht-Admins serverseitig aus
 * jedem `select`. Es taugt deshalb NICHT als Beleg einer Anlage: es wird
 * weder zum Verknuepfen angeboten (`verknuepfbareDokumente`) noch als Anlage
 * uebernommen (`anlageAusDokument` gibt `null` zurueck). Ein bereits
 * bestehender Verweis auf ein spaeter gesperrtes Dokument taucht im Abgleich
 * als `verwaist` auf, damit ihn jemand aufloest.
 *
 * Der GEDRUCKTE Plan entscheidet dagegen allein an `dokument_id` und bekommt
 * die Ablageliste gar nicht zu sehen. Absicht: derselbe Plan muss fuer jeden
 * Leser dasselbe sagen. Haenge der Nachweis an der Sichtbarkeit, druckte ein
 * Admin „liegt vor" und ein Bauleiter „nicht abgelegt" — aus demselben
 * Datenbestand.
 */

// ═══════════════════════════════════════════════════════════════════════════
// NACHWEISSTAND
// ═══════════════════════════════════════════════════════════════════════════

export type NachweisStand = 'liegt_vor' | 'wird_nachgereicht' | 'nicht_abgelegt';

/** Drucktexte. Sie stehen hier, weil sie zur Aussage gehoeren, nicht zum Satz. */
const NACHWEIS_TEXT: Record<NachweisStand, string> = {
    liegt_vor: 'liegt vor',
    wird_nachgereicht: 'wird nachgereicht',
    nicht_abgelegt: 'nicht in der Ablage',
};

export interface AnlagenZeile {
    id: string;
    art: 'grundlage' | 'anlage';
    nr: string;
    herausgeber: string;
    bezeichnung: string;
    kennung: string;
    datum: string | null;
    dokument_id: string | null;
    stand: NachweisStand;
    /** Was in der Nachweisspalte steht. */
    nachweis: string;
    /** Rot im Plan: hier fehlt etwas. */
    offen: boolean;
}

/**
 * Der Nachweisstand einer einzelnen Unterlage.
 *
 * Die Ablage schlaegt das Kennzeichen: liegt das Dokument vor, ist es
 * gleichgueltig, ob jemand vergessen hat, den Haken „wird nachgereicht"
 * wieder wegzunehmen. Der widerspruechliche Haken geht nicht verloren — er
 * erscheint im Abgleich (`widersprueche`) und laesst sich dort ausraeumen.
 */
export const nachweisStand = (u: Pick<Unterlage, 'dokument_id' | 'nachgereicht'>): NachweisStand => {
    if (u.dokument_id) return 'liegt_vor';
    if (u.nachgereicht) return 'wird_nachgereicht';
    return 'nicht_abgelegt';
};

/** Die Zeilen einer Art (Kapitel 9 druckt Grundlagen und Anlagen getrennt). */
export const anlagenZeilen = (
    unterlagen: readonly Unterlage[],
    art: 'grundlage' | 'anlage',
): AnlagenZeile[] =>
    (unterlagen || [])
        .filter(u => u && u.art === art)
        .map(u => {
            const stand = nachweisStand(u);
            return {
                id: u.id,
                art: u.art,
                nr: u.nr || '',
                herausgeber: u.herausgeber || '',
                bezeichnung: u.bezeichnung || '',
                kennung: u.kennung || '',
                datum: u.datum ?? null,
                dokument_id: u.dokument_id ?? null,
                stand,
                nachweis: NACHWEIS_TEXT[stand],
                offen: stand !== 'liegt_vor',
            };
        });

/**
 * Der rote Hinweis unter der Anlagenliste.
 *
 * Er benennt die offenen Stuecke namentlich statt sie zu zaehlen: „3 Anlagen
 * fehlen" zwingt zum Nachsehen, „Bodengutachten fehlt" nicht. `null` heisst,
 * dass nichts offen ist — dann steht auch kein Kasten auf dem Blatt.
 */
export const offenerNachweisHinweis = (zeilen: readonly AnlagenZeile[]): string | null => {
    const offen = (zeilen || []).filter(z => z.offen);
    if (offen.length === 0) return null;
    const teile = offen.map(z => `${z.bezeichnung || 'ohne Bezeichnung'} (${z.nachweis})`);
    return 'OFFEN: Diese Unterlagen sind in der Projektablage nicht belegt: '
        + `${teile.join('; ')}.`;
};

// ═══════════════════════════════════════════════════════════════════════════
// DRUCKSTUECKE VORMERKEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Aushaenge und Formulare entstehen im BROWSERDRUCK und nicht als Datei. Es
// gibt also nichts hochzuladen, solange niemand gedruckt hat. Der ehrliche
// Weg ist die Vormerkung: der Eintrag steht in der Anlagenliste, sein
// Nachweisstand ist `wird_nachgereicht`, und wer das ausgefuellte Blatt
// eingescannt hat, laedt es in die Projektablage und verknuepft es.
//
// Ein jsPDF-Nachbau der Aushaenge waere die Alternative — und genau der
// Fehler, den die Skizzen-Doktrin dieses Moduls ausschliesst: nie zweimal
// zeichnen. Zwei Zeichenwege driften auseinander, und der Unterschied faellt
// erst am Bauzaun auf.

export type Druckgattung = 'aushang' | 'formular';

/** Erster Abschnitt der Kennung eines vorgemerkten Druckstuecks. */
export const DRUCKSTUECK_PRAEFIX = 'asplan-druckstueck';

const TRENNER = ':';

export interface Druckstueck {
    gattung: Druckgattung;
    /** Schluessel aus `aushangKatalog` bzw. `formularKatalog`. */
    key: string;
    titel: string;
}

/**
 * Die maschinelle Kennung einer Vormerkung.
 *
 * Sie landet in `Unterlage.kennung`. Das Feld wird nur bei Grundlagen
 * gedruckt (Kapitel 9, Spalte „Kennung"), Vormerkungen sind Anlagen — es
 * steht also nichts Technisches auf dem Blatt.
 */
export const druckstueckKennung = (gattung: Druckgattung, key: string): string =>
    [DRUCKSTUECK_PRAEFIX, gattung, key].join(TRENNER);

/**
 * Liest eine Kennung zurueck. `null`, wenn es keine Vormerkung ist.
 *
 * Verglichen wird der erste ABSCHNITT und nicht der Anfang der Zeichenkette:
 * ein `startsWith`-Vergleich traf in diesem Repo schon einmal daneben
 * (`like 'asplan%'` trifft `asplaene` nicht, aber `asplanx` sehr wohl). Wer
 * hier abschnittsweise vergleicht, kann sich nicht vertun.
 */
export const leseDruckstueckKennung = (
    kennung: string | null | undefined,
): { gattung: Druckgattung; key: string } | null => {
    const teile = String(kennung || '').split(TRENNER);
    if (teile.length < 3) return null;
    if (teile[0] !== DRUCKSTUECK_PRAEFIX) return null;
    if (teile[1] !== 'aushang' && teile[1] !== 'formular') return null;
    const key = teile.slice(2).join(TRENNER);
    if (!key) return null;
    return { gattung: teile[1], key };
};

/** Eine neue Zeile fuer `asplan_unterlagen`, ohne die Schluessel des Bundles. */
export type UnterlagenEntwurf = Omit<Unterlage, 'id' | 'project_id' | 'asplan_id'>;

/**
 * Die Vormerkung eines Druckstuecks.
 *
 * `nachgereicht: true` ist hier richtig und keine Vorsichtsmassnahme: das
 * Blatt existiert im Augenblick der Vormerkung noch gar nicht. Es liegt erst
 * vor, wenn das gedruckte — und bei Formularen unterschriebene — Exemplar in
 * der Projektablage liegt und verknuepft ist.
 */
export const vormerkungEntwurf = (d: Druckstueck, sortierung: number): UnterlagenEntwurf => ({
    art: 'anlage',
    nr: '',
    herausgeber: '',
    bezeichnung: d.titel,
    datum: null,
    kennung: druckstueckKennung(d.gattung, d.key),
    dokument_id: null,
    nachgereicht: true,
    sortierung,
});

/** Die Druckstuecke, die noch nicht in der Anlagenliste stehen. */
export const offeneVormerkungen = (
    angebot: readonly Druckstueck[],
    unterlagen: readonly Unterlage[],
): Druckstueck[] => {
    const vergeben = new Set(
        (unterlagen || []).map(u => u && u.kennung).filter(Boolean) as string[],
    );
    return (angebot || []).filter(d => !vergeben.has(druckstueckKennung(d.gattung, d.key)));
};

// ═══════════════════════════════════════════════════════════════════════════
// ABLAGE DES PLAN-PDF
// ═══════════════════════════════════════════════════════════════════════════

/** Was die Anlagenliste von einem Dokument der Projektablage wissen muss. */
export interface AblageDokument {
    id: string;
    titel: string;
    dateiname?: string;
    nur_admin?: boolean;
}

/** Unterordner der Kategorie „Pläne", in dem die Fassungen des Plans liegen. */
export const ASPLAN_ORDNER = 'A+S-Plan';

const tagDeutsch = (iso?: string | null): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/**
 * Der Titel, unter dem eine Fassung des Plans in der Ablage steht.
 *
 * Die Revisionskennung gehoert IN den Titel und nicht nur in das Feld
 * `stand`: die Ablage sortiert nach Titel, und eine Liste mit fuenfmal
 * „A+S-Plan" laesst sich nicht lesen. Ohne Revisionskennung tritt der Stand
 * an ihre Stelle — ein Entwurf hat noch keine.
 */
const PLAN_TITEL = 'A+S-Plan';

/** Der Klammerzusatz des Titels: Revision (oder Ersatzwort) und Stand. */
const planTitelZusatz = (revLabel?: string | null, standIso?: string | null, ohneRevision = ''): string => {
    const rev = String(revLabel || '').trim();
    const tag = tagDeutsch(standIso);
    return [rev || ohneRevision, tag ? `Stand ${tag}` : ''].filter(Boolean).join(', ');
};

export const planAblageTitel = (revLabel?: string | null, standIso?: string | null): string =>
    `${PLAN_TITEL} (${planTitelZusatz(revLabel, standIso, 'Entwurf')})`;

/**
 * Der Ablage-Entwurf einer Planfassung, wie ihn der ALTBESTAND traegt.
 *
 * Bis zur zentralen Ablage (Plan Stufe 1A) schrieb `legeAsplanPdfAb` genau
 * diese Zeile ueber `uploadDokument`: Kategorie „Plaene", Ordner `A+S-Plan`,
 * Modul `asplan`. Daran erkennen zwei Stellen den Altbestand — der Rueckfall
 * `istErzeugteUnterlage` (dokumenteLogic.ts) und die Nachkennzeichnung im
 * SQL. Deshalb bleibt die Funktion stehen und unveraendert, obwohl der
 * Schreibweg heute `legeUnterlageAb` ist (`planAblageAngaben`): sie ist der
 * Fingerabdruck, gegen den die Tests beider Stellen pruefen.
 *
 * `nurAdmin` bleibt ausdruecklich `false`. Der A+S-Plan ist das Blatt, nach
 * dem auf der Baustelle gearbeitet wird; ihn hinter das Admin-Schloss zu
 * legen hiesse, ihn genau den Leuten zu entziehen, fuer die er geschrieben
 * ist. Wer eine einzelne Fassung sperren will, tut das nachtraeglich in der
 * Projektablage — und nimmt dann in Kauf, dass sie als Nachweis ausfaellt.
 */
export const planAblageEntwurf = (args: {
    revLabel?: string | null;
    standIso?: string | null;
    katalogVersion?: string | null;
}): DokumentEntwurf => ({
    kategorie: 'plaene',
    ordner: ASPLAN_ORDNER,
    titel: planAblageTitel(args.revLabel, args.standIso),
    beschreibung: 'Arbeits- und Sicherheitsplan nach DGUV Regel 101-004 / TRGS 524'
        + (args.katalogVersion ? `, Katalogfassung ${args.katalogVersion}` : ''),
    tags: ['A+S-Plan', 'DGUV Regel 101-004'],
    module: ['asplan'],
    stand: String(args.revLabel || '').trim() || undefined,
    nurAdmin: false,
});

/** Was der zentrale Ablageweg (`legeUnterlageAb`) von einer Planfassung wissen muss. */
export interface PlanAblageAngaben {
    titel: string;
    stand?: string;
    beschreibung: string;
    /** Fassungsbezug — nur eine FREIGEGEBENE Fassung hat einen. */
    fassungRef: string | null;
    anlass: 'freigabe' | 'entwurf';
}

/**
 * Die Angaben einer Planfassung fuer den zentralen Ablageweg.
 *
 * Stand und Beschreibung sind dieselben wie im Altbestand
 * (`planAblageEntwurf`); der Titel einer FREIGEGEBENEN Fassung ebenso — sie
 * soll in der Liste neben einer alten stehen koennen, ohne dass man ihr
 * ansieht, ueber welchen Weg sie kam. Ein ENTWURF traegt das Wort „Entwurf"
 * dagegen nicht mehr in der Klammer: der zentrale Ablageweg stellt bei
 * `anlass: 'entwurf'` selbst „ENTWURF — " voran, und „ENTWURF — A+S-Plan
 * (Entwurf, …)" sagte es zweimal.
 *
 * ═══ FASSUNGSBEZUG NUR BEI FREIGABE ═══════════════════════════════════════
 * `fassungRef` ist die Revisionskennung, unter der freigegeben wurde; fehlt
 * sie, der Freigabezeitpunkt. Ein ENTWURF bekommt keinen: er hat keinen
 * festen Stand, und wer ihn nach Aenderungen noch einmal ablegt, will eine
 * weitere Zeile — kein „schon abgelegt". Eine freigegebene Fassung ist
 * dagegen eingefroren; dieselbe Kennung zweimal heisst dasselbe Blatt, und
 * die Ablage darf die vorhandene Zeile zurueckgeben.
 *
 * Der Anlass folgt dem Status: Entscheid E17 — automatisch nur bei Freigabe,
 * Entwuerfe ausdruecklich per Knopf und als Entwurf gekennzeichnet.
 */
export const planAblageAngaben = (args: {
    revLabel?: string | null;
    standIso?: string | null;
    katalogVersion?: string | null;
    status?: 'entwurf' | 'freigegeben' | null;
    freigegebenAm?: string | null;
}): PlanAblageAngaben => {
    const e = planAblageEntwurf(args);
    const freigegeben = args.status === 'freigegeben';
    const rev = String(args.revLabel || '').trim();
    const am = String(args.freigegebenAm || '').trim();
    const entwurfZusatz = planTitelZusatz(args.revLabel, args.standIso);
    return {
        titel: freigegeben
            ? e.titel
            : (entwurfZusatz ? `${PLAN_TITEL} (${entwurfZusatz})` : PLAN_TITEL),
        stand: e.stand,
        beschreibung: e.beschreibung || '',
        fassungRef: freigegeben ? (rev || am || null) : null,
        anlass: freigegeben ? 'freigabe' : 'entwurf',
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// ABGLEICH ABLAGE ↔ ANLAGENLISTE
// ═══════════════════════════════════════════════════════════════════════════

export interface AblageAbgleich {
    /** Verweis auf ein Dokument, das die Ablage nicht (mehr) liefert. */
    verwaist: AnlagenZeile[];
    /** Dokumente des Moduls, auf die keine Unterlage zeigt. */
    unverknuepft: AblageDokument[];
    /** „wird nachgereicht" gekennzeichnet, obwohl das Dokument vorliegt. */
    widersprueche: AnlagenZeile[];
}

/** Nur diese Dokumente taugen als Nachweis (siehe Kopfkommentar). */
export const verknuepfbareDokumente = (
    dokumente: readonly AblageDokument[],
): AblageDokument[] => (dokumente || []).filter(d => d && d.nur_admin !== true);

/**
 * Was zwischen Ablage und Anlagenliste nicht zusammenpasst.
 *
 * `verwaist` faengt zwei Faelle in einem: ein geloeschtes Dokument und ein
 * nachtraeglich admin-gesperrtes. Fuer die Anlagenliste sind sie dasselbe —
 * der Nachweis ist nicht mehr einsehbar. Unterschieden werden muessen sie
 * erst, wenn jemand etwas dagegen tun will, und dann sagt es die Ablage.
 */
export const abgleicheAblage = (
    unterlagen: readonly Unterlage[],
    dokumente: readonly AblageDokument[],
): AblageAbgleich => {
    const nutzbar = verknuepfbareDokumente(dokumente);
    const nutzbareIds = new Set(nutzbar.map(d => d.id));
    const verwiesen = new Set(
        (unterlagen || []).map(u => u && u.dokument_id).filter(Boolean) as string[],
    );

    const alle = [
        ...anlagenZeilen(unterlagen, 'grundlage'),
        ...anlagenZeilen(unterlagen, 'anlage'),
    ];

    return {
        verwaist: alle.filter(z => z.dokument_id && !nutzbareIds.has(z.dokument_id)),
        unverknuepft: nutzbar.filter(d => !verwiesen.has(d.id)),
        widersprueche: (unterlagen || [])
            .filter(u => u && u.dokument_id && u.nachgereicht)
            .map(u => anlagenZeilen([u], u.art)[0]),
    };
};

/**
 * Die Aenderung, die eine Unterlage mit einem abgelegten Dokument verbindet.
 *
 * Das Kennzeichen „wird nachgereicht" faellt dabei — sonst blieben zwei
 * einander widersprechende Angaben nebeneinander stehen, und Kapitel 9
 * druckte je nach Auslesereihenfolge das eine oder das andere.
 */
export const verknuepfung = (dokumentId: string): Partial<Unterlage> => ({
    dokument_id: dokumentId,
    nachgereicht: false,
});

/**
 * Eine neue Anlage aus einem Dokument der Projektablage.
 *
 * `null` fuer ein admin-gesperrtes Dokument: sein Titel wuerde als
 * `bezeichnung` in den gedruckten Plan wandern und damit an jeden Leser, dem
 * die Sperre das Dokument gerade entzieht. Genau dieser Fehler ist im
 * Handbuch-Modul schon einmal gedruckt worden.
 */
export const anlageAusDokument = (
    d: AblageDokument,
    sortierung: number,
): UnterlagenEntwurf | null => {
    if (!d || d.nur_admin === true) return null;
    return {
        art: 'anlage',
        nr: '',
        herausgeber: '',
        bezeichnung: d.titel || d.dateiname || 'Dokument ohne Titel',
        datum: null,
        kennung: '',
        dokument_id: d.id,
        nachgereicht: false,
        sortierung,
    };
};
